import { FormGroup } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import Typography from '@material-ui/core/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/app/infra/auth';
import { useRoute } from 'src/app/infra/routes';
import { postResposta } from 'src/app/services/votacao';
import { BtnLoading, Msgbox, Optional } from 'src/app/shared/utils';
import {
  EEventoPosVoto,
  EFormaApresResultado,
  ESituacoes,
  ETiposResposta,
  IdentidadeVisual,
  Pergunta
} from 'src/app/types/votacao';
import { useWrapper } from 'src/utils/axios';
import styled from 'styled-components';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import Result from './result';

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`;

const StyledColumn = styled(Column)`
  > *:not(:first-child) {
    margin-top: 0.5rem;
  }
`;

const Title = styled(Typography)`
  font-weight: bold;
  text-align: start;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const Link = styled.button`
  color: #1492e6;
  font-size: 11px;
  text-decoration: underline;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

interface Props {
  item: Pergunta;
  selected: boolean;
  resultType?: EFormaApresResultado;
  identidadeVisual?: IdentidadeVisual;
  onSuccess: () => void;
}

export default ({
  item,
  selected,
  resultType,
  identidadeVisual,
  onSuccess
}: Props) => {
  const { session } = useAuth();
  const {
    params: { condominio }
  } = useRoute();
  const [redit, setRedit] = useState(false);

  const sendReq = useWrapper();

  const [votoUsuario, setVotoUsuario] = useState<string[]>(
    trataVotoUsuario(item.votoUsuario)
  );
  const [justificativa, setJustificativa] = useState(item.justificativa || '');

  const blockReason = useMemo(() => {
    if (item.situacao?.codigoSituacao !== ESituacoes.VotacaoAberta) {
      return 'Você poderá votar quando a pergunta estiver "aberta"!';
    } else if (!item.podeVotar?.habilitado) {
      return item.podeVotar.motivoDesabilitado;
    } else if (!votoUsuario?.length) {
      return 'Selecione uma opção para continuar!';
    }
    return '';
  }, [item, votoUsuario]);

  const hasVisibleOptions = useMemo(() => {
    switch (item.situacao?.codigoSituacao) {
      case ESituacoes.InicioPendente:
      case ESituacoes.VotacaoAberta:
        return true;
    }
    return false;
  }, [item]);

  const handleChange = useCallback(
    (resposta: string) => {
      if (item.tipoResposta === ETiposResposta.MultiplaEscolha) {
        const exist = votoUsuario.includes(resposta);
        if (exist) {
          setVotoUsuario(votoUsuario.filter((x) => x !== resposta));
        } else {
          setVotoUsuario([...votoUsuario, resposta]);
        }
      } else {
        setVotoUsuario([resposta]);
      }
    },
    [item, votoUsuario]
  );

  const sendVote = useCallback(() => {
    if (!session) return;

    sendReq.exec(
      postResposta({
        codigoVotacao: item.codigoVotacao,
        codigoPergunta: item.codigoPergunta,
        condominio: Number(condominio),
        unidade: session.user.unidade,
        respostas: votoUsuario,
        justificativa
      }),
      (res) => {
        if (res === 0) {
          toastr.options = {
            positionClass: 'toast-bottom-full-width',
            hideDuration: 300,
            timeOut: 60000
          };
          toastr.success('Voto enviado');
        }
        setRedit(false);
        onSuccess();
      },
      undefined,
      {
        defaultErrorMsg:
          'Tivemos um problema ao enviar seu voto, aguarde um momento e tente novamente!'
      }
    );
  }, [item, session, sendReq, condominio, votoUsuario]);

  //? remove opcoes da resposta que foram removidos da enquete
  useEffect(() => {
    if (!item.opcoes) return;

    setVotoUsuario(
      votoUsuario.filter((x) => item.opcoes.some((y) => y.textoResposta === x))
    );
  }, [item]);

  useEffect(() => {
    return () => sendReq.cancel();
  }, []);

  return (
    <StyledColumn>
      {item.votoUsuario && !redit ? (
        <Column style={{ alignItems: 'center' }}>
          <Title
            variant="caption"
            style={{ color: '#3ACC6C', margin: '1rem 0' }}
          >
            Voto enviado
          </Title>
          <Typography color="primary">
            Seu voto:{' '}
            {item.votoUsuario.length > 1
              ? Array(item.votoUsuario).join(', ')
              : item.votoUsuario}
          </Typography>
          <Optional
            valid={
              item.eventoPosVoto === EEventoPosVoto.PermiteAlterarAteFechar &&
              !blockReason
            }
          >
            <Row>
              <Link onClick={() => setRedit(true)}>Alterar voto</Link>
            </Row>
          </Optional>
        </Column>
      ) : (
        hasVisibleOptions && (
          <>
            <FormControl component="fieldset">
              {item.tipoResposta === ETiposResposta.MultiplaEscolha ? (
                <FormGroup>
                  {item.opcoes?.map((item, index) => (
                    <Tooltip
                      key={index}
                      title={item.comentario}
                      placement="bottom-start"
                      arrow
                    >
                      <span>
                        <FormControlLabel
                          label={item.textoResposta}
                          color={identidadeVisual?.headerColor || '#083F8B'}
                          control={
                            <input
                              type="checkbox"
                              checked={votoUsuario.includes(item.textoResposta)}
                              onChange={() => handleChange(item.textoResposta)}
                              style={{
                                minWidth: '22px',
                                minHeight: '25px',
                                marginLeft: '12px',
                                marginRight: '6px'
                              }}
                            />
                          }
                        />
                      </span>
                    </Tooltip>
                  ))}
                  <FormHelperText style={{ color: '#000' }}>
                    {item.quantMinimaEscolha === item.quantMaximaEscolha ? (
                      <div>
                        Selecione
                        <span
                          style={{
                            color: identidadeVisual?.headerColor || '#083F8B',
                            fontWeight: 'bold'
                          }}
                        >
                          {' '}
                          {item.quantMinimaEscolha}{' '}
                        </span>
                        opções
                      </div>
                    ) : (
                      <div>
                        Selecione no minimo
                        <span
                          style={{
                            color: identidadeVisual?.headerColor || '#083F8B',
                            fontWeight: 'bold'
                          }}
                        >
                          {' '}
                          {item.quantMinimaEscolha}{' '}
                        </span>
                        opçõa(es) e no máximo
                        <span
                          style={{
                            color: identidadeVisual?.headerColor || '#083F8B',
                            fontWeight: 'bold'
                          }}
                        >
                          {' '}
                          {item.quantMaximaEscolha}{' '}
                        </span>
                      </div>
                    )}
                  </FormHelperText>
                </FormGroup>
              ) : (
                <RadioGroup
                  value={votoUsuario[0]}
                  onChange={(e) => handleChange(e.target.value)}
                >
                  {item.opcoes.map((opcao, index) => (
                    <Tooltip
                      key={index}
                      title={opcao.comentario}
                      placement="bottom-start"
                      arrow
                    >
                      <span>
                        <FormControlLabel
                          label={opcao.textoResposta}
                          value={opcao.textoResposta}
                          control={
                            <Radio
                              color="primary"
                              style={{
                                color:
                                  identidadeVisual?.headerColor || '#083F8B'
                              }}
                            />
                          }
                        />
                      </span>
                    </Tooltip>
                  ))}
                </RadioGroup>
              )}
            </FormControl>

            <Optional valid={item.solicitaJustificaNaResposta}>
              <Title>Justificar</Title>
              <FormControl variant="outlined" fullWidth>
                <OutlinedInput
                  multiline
                  fullWidth
                  placeholder="*Digite a sua justificativa..."
                  rows={2}
                  rowsMax={3}
                  inputProps={{ maxLength: 100 }}
                  value={justificativa}
                  style={{ backgroundColor: 'white' }}
                  onChange={(e) => setJustificativa(e.target.value)}
                />
                <FormHelperText style={{ textAlign: 'right' }}>
                  {justificativa.length}/100
                </FormHelperText>
              </FormControl>
            </Optional>
            <Row>
              <Tooltip title={blockReason}>
                <span>
                  <BtnLoading
                    customColor
                    backgroundColor={
                      identidadeVisual?.['headerColor'] || '#083F8B'
                    }
                    disabled={
                      !!blockReason ||
                      session?.user?.unidadeLogin?.tipoUsuario === 4
                    }
                    loading={sendReq.loading}
                    onClick={sendVote}
                  >
                    Votar
                  </BtnLoading>
                  {session?.user?.unidadeLogin?.tipoUsuario !== 4 ? null : (
                    <FormHelperText
                      style={{
                        color: identidadeVisual?.headerColor || '#083F8B'
                      }}
                    >
                      Você não tem permissão para votar!
                    </FormHelperText>
                  )}
                </span>
              </Tooltip>
            </Row>
          </>
        )
      )}
      <Optional
        valid={
          selected &&
          (item.situacao.codigoSituacao === ESituacoes.VotacaoFinalizada ||
            (item.eventoPosVoto === EEventoPosVoto.VerResultadoParcial &&
              !!item.votoUsuario))
        }
      >
        <Column style={{ alignItems: 'center' }}>
          {item.eventoPosVoto === EEventoPosVoto.VerResultadoParcial && (
            <Title variant="caption">Resultado parcial</Title>
          )}
          <Result item={item} type={resultType} />
        </Column>
      </Optional>
      <Msgbox error={sendReq.error} onClose={() => sendReq.reset()} />
    </StyledColumn>
  );
};

function trataVotoUsuario(v: string[] | string | undefined) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return [v];
}
